// Advise Module Types — Supplier comparison and evaluation platform

// ============================================================
// Product Type Configuration (multi-product extensibility)
// ============================================================

export interface AdviseProductType {
  id: string;
  name: string;
  description: string;
  roiCalculators: RoiCalculatorType[];
}

export type RoiCalculatorType = 'baseline' | 'conversational_ai' | 'automated_qm' | 'real_time_assist';

// ============================================================
// Core Entities
// ============================================================

export interface AdviseCustomer {
  id: string;
  account_id: string;
  sf_customer_id?: string;
  sf_customer_name?: string;
  company_name: string;
  contact_person: string;
  email: string;
  phone: string;
  company_address: string;
  industry: string;
  company_size: string;
  status: string;
  budget_range: string;
  timeline: string;
  num_agents: number;
  priority_requirements: string[];
  notes: string;
  company_logo: string;
  created_date: string;
}

export interface AdviseSupplier {
  id: string;
  supplier_name: string;
  supplier_logo: string;
  sf_supplier_id?: string | null;
  sf_supplier_name?: string | null;
  product_category: string;
  headquarters: string;
  pricing_model: string;
  strengths: string[];
  limitations: string[];
  channel_manager_name: string;
  channel_manager_email: string;
  channel_manager_phone: string;
  created_date: string;
}

/** A Salesforce supplier, merged with its Advise record once it has one, or an Advise-only supplier. */
export interface SupplierDirectoryRow {
  // Null until the Salesforce supplier has an Advise record.
  id: string | null;
  sf_supplier_id: string | null;
  supplier_name: string;
  location: string | null;
  pricing_model: string | null;
  categories: string[];
}

export type SupplierSelectionEntry = { id: string } | { sf_supplier_id: string };

export interface SupplierSelectionSaved {
  selected_suppliers: string[];
  // The shortlisted suppliers in order, including any Advise record made for a Salesforce supplier.
  suppliers: AdviseSupplier[];
}

export type FeatureResponseState = 'supported' | 'not_supported' | 'partial';

export interface AdviseFeatureResponse {
  id: string;
  supplier_id: string;
  feature_key: string;
  state: FeatureResponseState;
  // The supplier's Response as they wrote it.
  answer: string;
  note: string | null;
  // Sent to UPSTACK staff only; a flagged answer stays out of Supplier Scoring until it is reviewed.
  flagged?: boolean;
}

export interface RequiredFeatureCategory {
  key: string;
  label: string;
  criterion: string;
  features: { key: string; label: string }[];
}

export interface FeatureInputSupplier {
  id: string;
  supplier_name: string;
  exported_at: string | null;
  imported_at: string | null;
  // Whether any of the supplier's answers is flagged; sent to every reader, unlike the flags themselves.
  awaiting_review: boolean;
}

export interface AdviseFeatureInput {
  required_categories: RequiredFeatureCategory[];
  suppliers: FeatureInputSupplier[];
  responses: AdviseFeatureResponse[];
}

export interface FeatureInputPreviewRow {
  feature_key: string;
  label: string;
  category: string;
  answer: string;
  state: FeatureResponseState;
  note: string | null;
  flagged: boolean;
  reason: string | null;
}

export interface FeatureInputPreview {
  file_name: string;
  supplier_id: string;
  features_in_file: number;
  // The exported features the file leaves blank or lists no row for, which the import leaves awaiting a response.
  unanswered: number;
  // Only the rows the supplier answered.
  rows: FeatureInputPreviewRow[];
}

export interface FeatureInputImported {
  supplier_id: string;
  imported: number;
  // The labels of the rows imported flagged.
  flagged: string[];
}

export type SupplierInvoiceStatus = 'needs_manual_entry' | 'parsed' | 'manual';

export interface AdviseSupplierLineItem {
  id: string;
  category: string;
  description?: string | null;
  quantity?: number | null;
  unit_cost?: number | null;
  monthly_cost?: number | null;
  in_scope: boolean;
  // Sent only to UPSTACK users.
  source?: 'invoice' | 'discovery' | 'advisor';
}

export interface AdviseSupplierInvoice {
  id: string;
  file_name: string;
  has_file: boolean;
  billing_period?: string | null;
  status: SupplierInvoiceStatus;
  taxes_and_surcharges: number;
  uploaded_at: string;
  line_items: AdviseSupplierLineItem[];
  // Sent back as `version` when replacing these line items; a stale one is refused with a 409.
  line_items_version: string;
}

export interface AdviseSupplierContract {
  end_date?: string | null;
  term_months?: number | null;
  auto_renew?: boolean | null;
  early_termination_fee?: number | null;
}

export interface AdviseCurrentSupplier {
  id: string;
  name: string;
  supplier_id?: string | null;
  spend_entry?: 'line_items' | 'monthly_total' | null;
  monthly_total?: number | null;
  include_taxes: boolean;
  contract: AdviseSupplierContract;
  invoices: AdviseSupplierInvoice[];
  line_items: AdviseSupplierLineItem[];
  line_items_version: string;
  // Calculated by the API; the screen never recalculates them.
  complete: boolean;
  most_recent_invoice_id: string | null;
  monthly_amount: number;
  out_of_scope_amount: number;
  taxes_excluded_amount: number;
  annualized_amount: number;
}

// Lines left out are deleted, and a listed line keeps whatever fields it doesn't send.
export interface SupplierLineItemsReplacement {
  version: string;
  line_items: SupplierLineItemInput[];
}

// A line without an id is added.
export type SupplierLineItemInput = Partial<Omit<AdviseSupplierLineItem, 'source'>> &
  Pick<AdviseSupplierLineItem, 'in_scope' | 'category'>;

export interface CurrentSupplierChange {
  spend_entry?: 'line_items' | 'monthly_total';
  monthly_total?: number | null;
  include_taxes?: boolean;
  contract?: AdviseSupplierContract;
}

// Presigned links that expire, so they are read when the file is opened rather than kept.
export interface SupplierInvoiceFileLinks {
  url: string;
  download_url: string;
  expires_at: string;
}

// The API refuses a supplier_id and an sf_supplier_id together; a free-text supplier sends neither.
export type NewCurrentSupplier = { name: string; spend_entry?: 'line_items' } & (
  | { supplier_id?: string; sf_supplier_id?: never }
  | { sf_supplier_id?: string; supplier_id?: never }
);

export interface AdviseCurrentSuppliers {
  skipped: boolean;
  suppliers?: AdviseCurrentSupplier[];
}

export type CurrentSpendStatus = 'step_off' | 'skipped' | 'sourced' | 'not_entered';

export type CurrentSpendBasis = 'invoice' | 'discovery' | 'manual' | 'monthly_total';

export interface CurrentSpendSource {
  supplier_id: string;
  name: string;
  monthly_amount: number;
  basis: CurrentSpendBasis;
  billing_period: string | null;
}

export interface CurrentSpendOverride {
  monthly_amount: number;
  sourced_monthly_amount: number;
  set_by: { id: number; name: string | null };
  set_at: string;
}

export interface AdviseCurrentSpend {
  status: CurrentSpendStatus;
  monthly_baseline: number | null;
  annualized_baseline: number | null;
  sources: CurrentSpendSource[];
  incomplete_suppliers: string[];
  by_category: { category: string; monthly_amount: number }[] | null;
  override: CurrentSpendOverride | null;
  effective_monthly_amount: number | null;
}

export interface AdviseEngagement {
  id: string;
  account_id: string;
  engagement_name: string;
  customer_id: string;
  product_type: string;
  selected_suppliers: string[];
  feature_notes: Record<string, Record<string, string>>;
  uploaded_quotes: AdviseQuote[];
  line_item_mapping: Record<string, Record<string, string>>;
  telephony_usage_overrides: Record<string, { quantity: number; rate: number }>;
  custom_price_categories: string[];
  roi_analysis: Record<string, unknown>;
  data_room_files: AdviseDataRoomFile[];
  data_room_notes: string;
  data_room_note_files: AdviseDataRoomFile[];
  opportunity_sf_id: string | null;
  opportunity_name: string | null;
  shared_with: string[];
  share_token?: string;
  mutual_action_plan: AdviseMutualActionPlan;
  map_view_mode: string;
  map_columns: Record<string, string>;
  report_settings: Record<string, unknown>;
  current_suppliers?: AdviseCurrentSuppliers;
  // Left out of the share link.
  current_spend?: AdviseCurrentSpend;
  status: string;
  owner_name: string;
  created_by: string;
  created_date: string;
  // Only the show response carries it; false when the caller may read the engagement but not change it.
  can_edit?: boolean;
}

export interface EngagementListItem {
  id: string;
  account_id: string;
  customer_id: string | null;
  customer_name: string | null;
  engagement_name: string;
  product_type: string;
  status: string;
  owner_name: string | null;
  created_by: number | null;
  created_date: string;
  opportunity_sf_id: string | null;
  opportunity_name: string | null;
}

export interface AdvisoryOpportunity {
  sf_id: string;
  name: string;
  stage: string | null;
  close_date: string | null;
  product_interest: string | null;
  account_id: string;
}

export interface MyOpportunities {
  salesforce_user_found: boolean;
  opportunities: AdvisoryOpportunity[];
  new_opportunity_stage: string;
}

interface NewEngagementFields {
  account_id: string;
  engagement: { engagement_name: string; product_type: string; customer_id?: string };
}

export type NewEngagementRequest = NewEngagementFields &
  ({ opportunity_sf_id: string } | { new_opportunity: { name: string; close_date: string } });

export interface AdviseTask {
  id: string;
  account_id: string;
  title: string;
  description: string;
  status: string;
  priority: string;
  due_date: string;
  engagement_id?: string;
  created_by: string;
  updated_date: string;
}

// ============================================================
// Sub-types
// ============================================================

export interface AdviseQuote {
  file_url: string;
  file_name: string;
  supplier_name: string;
  extracted_pricing: {
    supplier: string;
    summary: string;
    line_items: AdviseLineItem[];
  };
}

export interface AdviseLineItem {
  item_name: string;
  unit_price: number;
  quantity: number;
  total_price: number;
  billing_cycle: 'monthly' | 'one-time';
}

export interface AdviseDataRoomFile {
  file_url: string;
  file_name: string;
  section?: string;
  uploaded_date: string;
}

export interface AdviseMutualActionPlan {
  milestones: AdviseMilestone[];
}

export interface AdviseMilestone {
  id: string;
  milestone: string;
  notes: string;
  status: 'not_started' | 'in_progress' | 'complete';
  due_date: string;
  owner: 'Seller' | 'Buyer' | 'Partner';
  created_date: string;
}

export interface EngagementStep {
  key: string;
  label: string;
  description: string | null;
  required: boolean;
  enabled: boolean;
}

export interface EngagementConfiguration {
  steps: EngagementStep[];
  can_configure?: boolean;
}

export type DiscoveryFieldType = 'text' | 'notes' | 'number' | 'select' | 'yes_no' | 'multi_select';

// The whole saved answer to one question; custom_choices are the options added with "+ Add", as typed.
export interface DiscoveryAnswer {
  answer?: string | number | boolean | string[] | null;
  notes?: string | null;
  custom_choices?: string[];
}

export interface DiscoveryQuestion {
  id: string;
  key: string;
  label: string;
  field_type: DiscoveryFieldType;
  required: boolean;
  group: string | null;
  placeholder: string | null;
  choices: string[] | null;
  default: string | boolean | null;
  has_notes: boolean;
  custom_choice_label: string | null;
  value: DiscoveryAnswer | null;
}

export interface DiscoveryFormSection {
  id: string;
  key: string;
  label: string;
  questions: DiscoveryQuestion[];
}

export interface MatrixFeature {
  id: string;
  // What a supplier's imported answers are keyed by.
  key: string;
  label: string;
  custom: boolean;
  // Added with + Add in Discovery, so it is removed only with its option there.
  from_discovery: boolean;
  selected: boolean;
}

export interface MatrixCategory {
  id: string;
  key: string;
  label: string;
  custom: boolean;
  features: MatrixFeature[];
}

export interface MatrixCriterion {
  id: string;
  key: string;
  label: string;
  description: string | null;
  key_areas: string[];
  custom: boolean;
  selected: boolean;
  // A percentage in steps of 5; null while the criterion is cleared.
  weight: number | null;
  categories: MatrixCategory[];
}

export type MatrixCriterionValue = { selected: true; weight: number } | { selected: false };

export interface NewMatrixCriterion {
  label: string;
  description: string | null;
  // Names of the features to add to its category, each different.
  features: string[];
}

// A criterion with no category gets one named after it to hold the feature.
export type MatrixFeatureHome = { categoryId: string } | { criterionId: string };

export interface ScoringFunction {
  id: string;
  key: string;
  label: string;
}

export interface ScoringCategory {
  id: string;
  key: string;
  label: string;
  // The card's column heading, such as "Omni.".
  short_label: string;
  functions: ScoringFunction[];
}

export type ClientScoringStatus = 'not_started' | 'in_progress' | 'completed';

export interface ScoringSupplier {
  id: string;
  supplier_name: string;
  status: ClientScoringStatus;
}

// UPSTACK's rating of one supplier on one function; an unrated function has none.
export interface SupplierRating {
  supplier_id: string;
  function_id: string;
  rating: number;
  // Why the rating differs from the client average; null when it doesn't, or when no note was given yet.
  note: string | null;
  noted_by_name: string | null;
  noted_at: string | null;
}

// One client's submitted score; a draft is never one.
export interface ClientFunctionScore {
  request_id: string;
  first_name: string;
  last_name: string;
  role: string | null;
  submitted_at: string;
  score: number;
}

// The scores clients submitted for one supplier and function, oldest first.
export interface ClientScore {
  supplier_id: string;
  function_id: string;
  // Rounded half up.
  average: number;
  scores: ClientFunctionScore[];
}

export interface AdviseSupplierScoring {
  categories: ScoringCategory[];
  // The shortlist, alphabetically.
  suppliers: ScoringSupplier[];
  ratings: SupplierRating[];
  client_scores: ClientScore[];
}

export type ScoringRequestStatus = 'sent' | 'viewed' | 'completed' | 'reassigned' | 'not_delivered';

// One invitee's request to score a supplier, as Request Supplier Scoring lists it.
export interface ScoringRequestRow {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  role: string | null;
  // viewed once the scorer opened their link; not_delivered when the invite email failed.
  status: ScoringRequestStatus;
  all_categories: boolean;
  scope: { key: string; label: string }[];
  first_viewed_at: string | null;
  completed_at: string | null;
  // An open request whose link expired, or whose invite never went out: inviting the address again sends a new link.
  link_lapsed: boolean;
  reassigned_to: { first_name: string; last_name: string } | null;
}

export interface ScoringInvitee {
  first_name: string;
  last_name: string;
  email: string;
  role: string | null;
  // Category keys; empty means every category.
  scope: string[];
}

export interface ScoringInviteOutcome {
  email: string;
  sent: boolean;
  error: string | null;
}

export interface ScoringInviteResult {
  sent: number;
  results: ScoringInviteOutcome[];
}

export interface ScoringReassignResult {
  request: ScoringRequestRow;
}

// ============================================================
// Outside scorer (the public /score page)
// ============================================================

export interface ScorerAnswers {
  // 0–100 by function id; an unrated function is left out, never sent as a number.
  scores: Record<string, number>;
  comment: string | null;
}

export interface ScorerOpenView {
  state: 'open';
  invitee: { first_name: string; last_name: string; role: string | null };
  note: string | null;
  expires_at: string;
  engagement: { engagement_name: string; product_type: string };
  client: { company_name: string; company_logo: string | null } | null;
  supplier: { supplier_name: string; supplier_logo: string | null };
  categories: ScoringCategory[];
  // Whether the scope covers every Supplier Scoring category.
  all_categories: boolean;
  draft: ScorerAnswers & { saved_at: string | null };
}

export interface ScorerConfirmation {
  state: 'completed';
  completed_at: string | null;
  supplier: { supplier_name: string };
  categories: { key: string; label: string }[];
}

export type ScorerView = ScorerOpenView | ScorerConfirmation;

export interface ScorerDraftSaved {
  state: 'open';
  saved_at: string;
}

// ============================================================
// ROI Calculator Types
// ============================================================

export interface BaselineRoiInput {
  monthly_call_volume: number;
  number_of_agents: number;
  agent_annual_salary: number;
  cost_per_minute: number;
  supplier_licensing_cost: number;
  implementation_cost: number;
  aht_reduction_percent: number;
  auth_time_reduction_percent: number;
  chat_handle_time_reduction_percent: number;
  email_resolution_reduction_percent: number;
  chatbot_deflection_percent: number;
  conversion_improvement_percent: number;
  fte_reduction_percent: number;
  attrition_reduction_percent: number;
}

export interface BaselineRoiOutput {
  licensing_savings: number;
  aht_savings: number;
  auth_savings: number;
  chat_sms_savings: number;
  email_savings: number;
  chatbot_savings: number;
  total_labor_savings: number;
  total_cost_savings: number;
  revenue_uplift: number;
  op_efficiency: number;
  total_annual_benefit: number;
  net_benefit_year1: number;
  roi_percent: number;
  payback_months: number;
}

export interface ConversationalAiRoiInput {
  monthly_inbound: number;
  pct_eligible: number;
  containment_rate: number;
  aht_minutes: number;
  cost_per_minute: number;
  agent_cost_annual: number;
  annual_platform_cost: number;
  implementation_cost: number;
}

export interface ConversationalAiRoiOutput {
  automatable_monthly: number;
  ai_contained_monthly: number;
  savings_per_interaction: number;
  annual_hard_savings: number;
  agent_time_saved_hours_annual: number;
  labor_savings_annual: number;
  first_year_roi: number;
  payback_months: number;
  fte_equivalent: number;
}

export interface AutomatedQmRoiInput {
  monthly_interactions: number;
  percent_manual_qa: number;
  ai_eval_rate: number;
  ai_accuracy: number;
  current_analysts: number;
  analysts_required_with_ai: number;
  analyst_annual_cost: number;
  analyst_hourly_cost: number;
  monthly_time_saved: number;
  quality_improvement_value: number;
  annual_platform_cost: number;
  implementation_cost: number;
}

export interface AutomatedQmRoiOutput {
  manual_monthly_evals: number;
  analyst_reduction: number;
  annual_analyst_savings: number;
  annual_cost_savings: number;
  total_annual_benefit: number;
  net_benefit_year1: number;
  roi_pct: number;
}

export interface RealTimeAssistRoiInput {
  monthly_interactions: number;
  aht_minutes: number;
  cost_per_minute: number;
  num_agents: number;
  agent_annual_cost: number;
  aht_reduction_percent: number;
  ramp_time_reduction_percent: number;
  compliance_minutes_saved: number;
  compliance_improvement_percent: number;
  revenue_per_interaction: number;
  csat_improvement_percent: number;
  annual_platform_cost: number;
  implementation_cost: number;
}

export interface RealTimeAssistRoiOutput {
  baseline_labor_annual: number;
  aht_savings_annual: number;
  ramp_savings_annual: number;
  compliance_savings_annual: number;
  csat_revenue_impact: number;
  total_annual_benefit: number;
  net_benefit_year1: number;
  roi_pct: number;
}

// ============================================================
// Enums
// ============================================================

export const AdviseCustomerStatus = {
  DISCOVERY: 'discovery',
  REQUIREMENTS_GATHERING: 'requirements_gathering',
  SUPPLIER_EVALUATION: 'supplier_evaluation',
  PROPOSAL_STAGE: 'proposal_stage',
  NEGOTIATION: 'negotiation',
  CLOSED_WON: 'closed_won',
  CLOSED_LOST: 'closed_lost'
} as const;

export const AdviseCustomerIndustry = {
  TECHNOLOGY: 'Technology',
  HEALTHCARE: 'Healthcare',
  FINANCIAL_SERVICES: 'Financial Services',
  RETAIL: 'Retail',
  MANUFACTURING: 'Manufacturing',
  GOVERNMENT: 'Government',
  EDUCATION: 'Education',
  TELECOMMUNICATIONS: 'Telecommunications',
  ENERGY: 'Energy',
  MEDIA_ENTERTAINMENT: 'Media & Entertainment',
  OTHER: 'Other'
} as const;

export const AdviseCompanySize = {
  SMALL: '1-50',
  SMALL_MEDIUM: '51-200',
  MEDIUM: '201-1000',
  LARGE: '1001-5000',
  ENTERPRISE: '5000+'
} as const;

export const AdviseBudgetRange = {
  UNDER_50K: 'Under $50K',
  _50K_100K: '$50K - $100K',
  _100K_250K: '$100K - $250K',
  _250K_500K: '$250K - $500K',
  _500K_1M: '$500K - $1M',
  _1M_5M: '$1M - $5M',
  OVER_5M: 'Over $5M'
} as const;

export const EngagementStatus = {
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  PUBLISHED: 'published',
  IN_REVIEW: 'in_review'
} as const;

export type EngagementStatusValue = (typeof EngagementStatus)[keyof typeof EngagementStatus];

export const AdviseTaskStatus = {
  TODO: 'todo',
  IN_PROGRESS: 'in_progress',
  DONE: 'done'
} as const;

export const AdviseTaskPriority = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
} as const;

export const AdvisePricingModel = {
  PER_AGENT_MONTHLY: 'per_agent_monthly',
  PER_INTERACTION: 'per_interaction',
  TIERED_PRICING: 'tiered_pricing',
  ENTERPRISE_CUSTOM: 'enterprise_custom',
  USAGE_BASED: 'usage_based'
} as const;

export const AdviseMilestoneOwner = {
  SELLER: 'Seller',
  BUYER: 'Buyer',
  PARTNER: 'Partner'
} as const;

export const DEFAULT_PRICE_CATEGORIES = [
  'Licensing',
  'Telco / Usage',
  'Professional Services',
  'WFM',
  'Quality Management',
  'AI & Automation',
  'Analytics',
  'Recording & Storage',
  'Integration',
  'Support & Maintenance',
  'Other'
] as const;
